import classNames from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { toggleViewerInsightsUI } from 'actions/viewerInsights';
import { SHARE_CTA_INSTALL_SLACK_CLICKED } from 'constants/events';
import { ROLLOUT_FILE_ATTACHMENTS } from 'constants/featureFlag';
import { SLACK_APP } from 'constants/oAuthKeys';
import {
  ENGAGEMENT_INSIGHTS_PAYWALL,
  VIDEO_EDIT_OPTIONS_ACCESS,
} from 'constants/scopes';
import { completeTriggerCreator } from 'creators/triggers';
import * as analytics from 'utilities/analytics';
import * as auth from 'utilities/auth';
import { requestAnimationFrame } from 'utilities/dom';
import * as extension from 'utilities/extension';
import isUnsafeRedirectUrl from 'utilities/isUnsafeRedirectUrl';
import { getParam } from 'utilities/url';
import videoSession from 'utilities/video-session';

import { selectIsActive as attachmentsActive } from 'selectors/attachments';
import { selectEditorIsActive } from 'selectors/editor';
import { hasScope } from 'selectors/scopes';
import { selectVideo, selectVideoUseEmojis } from 'selectors/shareVideo';
import { selectIsActive as viewerInsightsActive } from 'selectors/viewerInsights';

import AttachmentWidget from '../attachment-widget';
import EditorWidget from '../editor-widget';
import SharePageCard from '../share-page-card';
import TranscriptHeader from '../transcript-list/transcript-header';
import TranscriptWrapper from '../transcript-list/transcript-wrapper/index';
import ViewerInsights from '../viewer-insights';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import Scopes from 'components/scopes';

import './styles.less';

class RightPanel extends Component {
  defaultStyle = {
    width: 'auto',
    height: 'auto',
    overflow: 'auto',
  };

  render() {
    const {
      speechFlow,
      theaterMode,
      useEmojis,
      userCanEdit,
      video,
      viewerInsightsActive,
      transcriptVisible,
      videoFrameHeight,
    } = this.props;

    return (
      <div
        className={classNames(
          (viewerInsightsActive || !theaterMode) && 'right-panel',
          {
            'speech-flow': speechFlow,
            'theater-mode': theaterMode,
            'use-emojis': useEmojis,
          }
        )}
      >
        {transcriptVisible && !theaterMode && videoFrameHeight && (
          <SharePageCard
            onRightPanel={true}
            height={videoFrameHeight}
            header={<TranscriptHeader onExit={this.props.onTranscriptExit} />}
          >
            <TranscriptWrapper />
          </SharePageCard>
        )}
        {userCanEdit && viewerInsightsActive && (
          <ViewerInsights onActivate={this.props.toggleViewerInsightsUI} />
        )}
        {userCanEdit && (
          <Scopes names={VIDEO_EDIT_OPTIONS_ACCESS}>
            <EditorWidget
              onEnterTrimMode={this.props.onEnterTrimMode}
              video={video}
            />
          </Scopes>
        )}
        {userCanEdit && (
          <FeatureFlaggedComponent
            flag={ROLLOUT_FILE_ATTACHMENTS}
            activationValues={[true]}
          >
            <Scopes names={VIDEO_EDIT_OPTIONS_ACCESS}>
              <AttachmentWidget />
            </Scopes>
          </FeatureFlaggedComponent>
        )}
      </div>
    );
  }

  connect(triggerHandler) {
    auth.openOAuthPopup(SLACK_APP, {}, data => {
      let redirectURL;
      const cameFrom = getParam('redirect_after');

      this.trackEvent(SHARE_CTA_INSTALL_SLACK_CLICKED);
      triggerHandler();

      redirectURL = decodeURIComponent(cameFrom);

      if (isUnsafeRedirectUrl(redirectURL)) {
        redirectURL = '/';
      }

      if (data.error != null) {
        redirectURL += '?oauth_error=' + data.error;
      }

      if (data.loginSuccess) {
        extension.signalExtensionLogin(() => {
          window.location.href = redirectURL;
        });
      } else {
        window.location.href = redirectURL;
      }
    });
  }

  componentDidUpdate(prevProps) {
    const isEnter = prop => !prevProps[prop] && this.props[prop];
    const isExit = prop => prevProps[prop] && !this.props[prop];

    if (isEnter('editorActive')) {
      videoSession.endSession(true);
      this.exitAnimation = this.getWidgetAnimeInstance('.edit-video');
    }

    if (isEnter('attachmentWidgetActive')) {
      videoSession.endSession(true);
      this.exitAnimation = this.getWidgetAnimeInstance('.attachment-widget');
    }

    if (isEnter('speechFlow')) {
      videoSession.endSession(true);
      this.exitAnimation = this.getWidgetAnimeInstance(
        '[widget="speech-coach"]'
      );
    }

    if (isEnter('viewerInsightsActive')) {
      videoSession.endSession(true);
      this.exitAnimation = this.getWidgetAnimeInstance('.viewer-insights');
    }

    const reverseAnime = [
      'editorActive',
      'viewerInsightsActive',
      'attachmentWidgetActive',
      'speechFlow',
    ].some(el => isExit(el));

    if (reverseAnime && this.exitAnimation) {
      this.exitAnimation();
    }
  }

  getWidgetAnimeInstance(unaffectedClass) {
    const bodyClass = unaffectedClass.substr(1) + '-active';
    const els = document.querySelectorAll(
      `.right-panel>*:not(${unaffectedClass})`
    );

    requestAnimationFrame(() => {
      els.forEach(el => el.classList.add('right-panel-faded-elements'));
      document.body.classList.add(bodyClass, 'right-panel-box-raised');
    });

    return () => {
      requestAnimationFrame(() => {
        document.body.classList.add('right-panel-box-back');
        document.body.classList.remove('right-panel-box-raised');
      });
      setTimeout(() => {
        requestAnimationFrame(() => {
          els.forEach(el => el.classList.remove('right-panel-faded-elements'));
          document.body.classList.remove(bodyClass, 'right-panel-box-back');
        });
      }, 250);
    };
  }

  trackEvent(type) {
    analytics.track(type, { video_id: this.props.video.id });
  }
}

const mapStateToProps = state => {
  const { videoFrameHeight } = state.video_player;

  return {
    attachmentWidgetActive: attachmentsActive(state),
    video: selectVideo(state),
    editorActive: selectEditorIsActive(state),
    speechFlow: state.speechCoach.speechFlow,
    useEmojis: selectVideoUseEmojis(state),
    viewerInsightsActive: viewerInsightsActive(state),
    hasWorkspaceEngagementPaywallsScope: hasScope(
      state,
      ENGAGEMENT_INSIGHTS_PAYWALL
    ),
    videoFrameHeight,
  };
};

const mapDispatchToProps = dispatch => ({
  completeTriggerCreator: trigger => dispatch(completeTriggerCreator(trigger)),
  toggleViewerInsightsUI: () => dispatch(toggleViewerInsightsUI()),
});

export default connect(mapStateToProps, mapDispatchToProps)(RightPanel);
